import { callLambda, type LambdaResponse } from './lambda.js';

// The calls behind Buy nodeStream credits and the PUBLIC_LEDGER team's Fund this test company by hand card. Each lambda
// checks the caller and recomputes every figure; a refusal throws with the lambda's message.

// public-ledger-credits-quote's answer, as the lambda sends it
export interface CreditQuoteAnswer {
  purchaseId: string;
  unitPricePence: number;
  expiresAt: string;
  minCredits?: number;
  // Credits nodeStream can send now above its reserve, and credits already owed to others on the network; null when
  // either can't be read (the card then shows no delivery message)
  availableCredits: number | null;
  owedCredits: number | null;
}

export interface CreditPayAnswer {
  // The payment intent's status: requires_payment_method (the card still to enter) or succeeded
  status?: string;
  clientSecret?: string;
}

function answer<T>(result: LambdaResponse, fallback: string): T {
  if (result.res.code !== 200) {
    throw new Error(typeof result.res.message === 'string' && result.res.message ? result.res.message : fallback);
  }
  return result.res.message as T;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// A whole number of credits the lambda sends as a string (or a number), or null
function wholeCredits(value: unknown): number | null {
  const n = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 ? n : null;
}

// A quote answer, or null when it isn't one
export function parseQuoteAnswer(value: unknown): CreditQuoteAnswer | null {
  if (!isRecord(value) || typeof value.purchaseId !== 'string' || !value.purchaseId) return null;
  const { unitPricePence, expiresAt } = value;
  if (typeof unitPricePence !== 'number' || !Number.isFinite(unitPricePence) || typeof expiresAt !== 'string')
    return null;
  return {
    purchaseId: value.purchaseId,
    unitPricePence,
    expiresAt,
    minCredits: typeof value.minCredits === 'number' ? value.minCredits : undefined,
    availableCredits: wholeCredits(value.availableCredits),
    owedCredits: wholeCredits(value.owedCredits),
  };
}

/**
 * The card's live quote through public-ledger-credits-quote: the price per credit held for 30 minutes at the mainnet rate,
 * and how many credits can be delivered now. Refused with PUBLIC_LEDGER_PURCHASE_TESTNET_ONLY for a MAINNET company
 * outside production.
 */
export async function quoteCredits(token: string | undefined, request: { jobId: string }): Promise<CreditQuoteAnswer> {
  const result = await callLambda('public-ledger/credits/quote', 'POST', token, JSON.stringify(request));
  const parsed = parseQuoteAnswer(answer<unknown>(result, 'Unable to get a price'));
  if (!parsed) throw new Error('Unable to get a price');
  return parsed;
}

/**
 * Starts the payment through public-ledger-credits-pay, which checks the amount and the card's live quote and computes the
 * total on the server. The card gets a client secret for the Payment Element: a card is always entered on the spot (#25
 * DECISION 2.7).
 */
export async function payCredits(
  token: string | undefined,
  request: { jobId: string; credits: number },
): Promise<CreditPayAnswer> {
  const result = await callLambda('public-ledger/credits/pay', 'POST', token, JSON.stringify(request));
  const value = answer<unknown>(result, 'Unable to start the payment');
  if (!isRecord(value)) return {};
  return {
    status: typeof value.status === 'string' ? value.status : undefined,
    clientSecret: typeof value.clientSecret === 'string' ? value.clientSecret : undefined,
  };
}

/**
 * Starts Buy nodeStream credits for the company's signatories team through public-ledger-credits-launch (D6), or opens the
 * one already open. Signatories only; refused with the testnet-only message for a MAINNET company outside production.
 */
export async function launchCreditPurchase(
  token: string | undefined,
  request: { companyId: string },
): Promise<unknown> {
  const result = await callLambda('public-ledger/credits/launch', 'POST', token, JSON.stringify(request));
  return answer<unknown>(result, 'Unable to start the purchase');
}

/**
 * Confirms the PUBLIC_LEDGER team refunded a purchase whose credits couldn't be delivered, through
 * public-ledger-credits-confirm-refund: the card's assignee only. It completes the card as refunded only once Stripe shows
 * the refund, and otherwise refuses with Stripe's figures.
 */
export async function confirmRefund(token: string | undefined, request: { jobId: string }): Promise<unknown> {
  const result = await callLambda('public-ledger/credits/confirm-refund', 'POST', token, JSON.stringify(request));
  return answer<unknown>(result, "The refund couldn't be confirmed");
}

/**
 * Confirms the PUBLIC_LEDGER team funded a MAINNET test company by hand, through public-ledger-credits-confirm-test-funding:
 * the card's assignee only. It reads the company account fresh, records the reading and completes the card.
 */
export async function confirmTestFunding(
  token: string | undefined,
  request: { jobId: string; fundedCredits: number },
): Promise<unknown> {
  const result = await callLambda('public-ledger/credits/confirm-test-funding', 'POST', token, JSON.stringify(request));
  return answer<unknown>(result, 'Unable to confirm the funding');
}
